import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Fingerprint, Barbell, Brain, Lightning, Timer,
  ArrowRight, Check, Play, Eye, Target, Crosshair,
  Heart, PersonSimpleRun, CaretUp, CaretDown, Warning
} from '@phosphor-icons/react';
import axios from 'axios';
import { TRICK_QUESTIONS_POOL, PRIORITY_SCENARIOS_POOL, pickRandom } from '../lib/bodyPools';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NoiseOverlay = () => <div className="noise-overlay" />;

// HUD Frame
const HudFrame = ({ label, children, color = 'var(--text-primary)' }) => (
  <div className="hud-frame" style={{ borderColor: color }}>
    <div className="hud-frame-label" style={{ color }}>{label}</div>
    {children}
  </div>
);

// ==================== TRICK QUESTIONS (System 1 vs System 2) ====================
// Default reference lists — kept as fallback & for tests (dynamic pool now lives in bodyPools.js)
const TRICK_QUESTIONS = TRICK_QUESTIONS_POOL.slice(0, 5);
const PRIORITY_SCENARIOS = PRIORITY_SCENARIOS_POOL.slice(0, 5).map((s, i) => ({ ...s, urgency: i + 1 }));

// ==================== MEMORY SYMBOLS ====================
const MEMORY_SYMBOLS = ['◆', '▲', '●', '■', '★', '◇', '▼'];
const MEMORY_DISTRACTOR = ['○', '□', '☆', '◈', '▽', '▶', '◀'];

// ==================== PHYSICAL SCORE CALCULATOR ====================
function calcPhysicalScore(data) {
  let score = 0;
  score += Math.min(30, (data.max_pushups || 0) * 0.6);
  score += Math.min(20, (data.max_plank_seconds || 0) * 0.15);
  score += Math.min(20, (data.weekly_km || 0) * 2);
  score += Math.min(15, (data.daily_steps || 0) / 700);
  if (data.resting_pulse > 0 && data.resting_pulse <= 60) score += 15;
  else if (data.resting_pulse > 60 && data.resting_pulse <= 75) score += 10;
  else if (data.resting_pulse > 75) score += 5;
  return Math.min(100, Math.round(score));
}

// ==========================================
// SECTION 1: PHYSICAL PROFILE (Form)
// ==========================================
const SectionPhysical = ({ data, setData, onComplete }) => {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="section-physical">
      <div className="text-center mb-8">
        <Barbell size={48} className="mx-auto mb-4" style={{ color: '#22C55E' }} />
        <h2 className="text-xl font-bold mb-2">PHYSISCHE BASIS</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Erfasse dein koerperliches Profil. Ehrlichkeit zahlt sich aus — die KI erkennt Diskrepanzen.
        </p>
      </div>

      <div className="space-y-5">
        <HudFrame label="KRAFT" color="#22C55E">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-overline block mb-1" style={{ fontSize: '0.6rem' }}>MAX. LIEGESTUETZE</label>
              <input type="number" value={data.max_pushups} onChange={e => setData(d => ({ ...d, max_pushups: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                placeholder="z.B. 25" className="input-brutal w-full" data-testid="input-pushups" />
            </div>
            <div>
              <label className="text-overline block mb-1" style={{ fontSize: '0.6rem' }}>MAX. PLANK / WALL-SIT (Sek.)</label>
              <input type="number" value={data.max_plank_seconds} onChange={e => setData(d => ({ ...d, max_plank_seconds: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                placeholder="z.B. 90" className="input-brutal w-full" data-testid="input-plank" />
            </div>
          </div>
        </HudFrame>

        <HudFrame label="AUSDAUER" color="#3B82F6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-overline block mb-1" style={{ fontSize: '0.6rem' }}>JOGGING KM / WOCHE</label>
              <input type="number" step="0.5" value={data.weekly_km} onChange={e => setData(d => ({ ...d, weekly_km: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                placeholder="z.B. 10" className="input-brutal w-full" data-testid="input-km" />
            </div>
            <div>
              <label className="text-overline block mb-1" style={{ fontSize: '0.6rem' }}>SCHRITTE / TAG</label>
              <input type="number" value={data.daily_steps} onChange={e => setData(d => ({ ...d, daily_steps: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                placeholder="z.B. 8000" className="input-brutal w-full" data-testid="input-steps" />
            </div>
          </div>
        </HudFrame>

        <HudFrame label="VITALWERTE" color="#8B5CF6">
          <div>
            <label className="text-overline block mb-1" style={{ fontSize: '0.6rem' }}>RUHEPULS (BPM) — OPTIONAL</label>
            <input type="number" value={data.resting_pulse} onChange={e => setData(d => ({ ...d, resting_pulse: e.target.value === '' ? '' : parseInt(e.target.value) }))}
              placeholder="z.B. 68" className="input-brutal w-full" data-testid="input-pulse" />
            <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
              Tipp: 2 Finger am Handgelenk, 15 Sek zaehlen, mal 4.
            </p>
          </div>
        </HudFrame>
      </div>

      <button onClick={onComplete} disabled={!data.max_pushups && !data.max_plank_seconds}
        className="btn-brutal btn-brutal-filled w-full flex items-center justify-center gap-3 mt-8"
        style={{ opacity: (data.max_pushups || data.max_plank_seconds) ? 1 : 0.35 }}
        data-testid="physical-next">
        Weiter ZUM KOGNITIVEN LABOR <ArrowRight size={20} weight="bold" />
      </button>
    </motion.div>
  );
};

// ==========================================
// SECTION 2A: PRIORITY MATRIX (15s Drag Sort)
// ==========================================
const SectionPriority = ({ onComplete, pool }) => {
  const sourceList = pool && pool.length ? pool : PRIORITY_SCENARIOS;
  const [phase, setPhase] = useState('intro');
  const [items, setItems] = useState(() => [...sourceList].sort(() => 0.5 - Math.random()));
  const [timeLeft, setTimeLeft] = useState(15);
  const intervalRef = useRef(null);

  useEffect(() => { return () => clearInterval(intervalRef.current); }, []);

  const startTest = () => {
    setPhase('active');
    intervalRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(intervalRef.current); setPhase('done'); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  const submitEarly = () => {
    clearInterval(intervalRef.current);
    setPhase('done');
  };

  const moveItem = (idx, dir) => {
    const arr = [...items];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setItems(arr);
  };

  useEffect(() => {
    if (phase === 'done') {
      const userOrder = items.map(i => i.id);
      const correctOrder = [...sourceList].sort((a, b) => a.urgency - b.urgency).map(i => i.id);
      let matches = 0;
      userOrder.forEach((id, idx) => { if (correctOrder[idx] === id) matches++; });
      onComplete({ user_order: userOrder, correct_order: correctOrder, matches, total: sourceList.length, time_used: 15 - timeLeft });
    }
    // Absichtlich nur [phase]: dieser Effekt darf NUR beim Übergang auf 'done' EINMAL feuern.
    // onComplete (Inline-Callback des Parents) in die Deps zu nehmen würde Mehrfach-Aufrufe/Loops auslösen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (phase === 'intro') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="priority-intro">
        <Target size={48} className="mx-auto mb-4" style={{ color: '#F59E0B' }} />
        <h2 className="text-xl font-bold mb-2">STATION A: PRIORITAETEN-MATRIX</h2>
        <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
          5 Szenarien erscheinen gleichzeitig. Sortiere sie nach Dringlichkeit — du hast <span style={{ color: 'var(--accent-cyan)' }}>15 Sekunden</span>.
        </p>
        <button onClick={startTest} className="btn-brutal btn-brutal-filled flex items-center justify-center gap-3 mx-auto" data-testid="priority-start">
          <Play size={20} weight="fill" /> STARTEN
        </button>
      </motion.div>
    );
  }

  if (phase === 'done') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-6" data-testid="priority-done">
        <Check size={48} className="mx-auto mb-3" style={{ color: '#22C55E' }} />
        <p className="text-mono" style={{ color: '#22C55E' }}>PRIORISIERUNG ERFASST</p>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="priority-active">
      <div className="hud-display text-center mb-4">
        <p className="text-overline">VERBLEIBENDE ZEIT</p>
        <p className="text-4xl font-black" style={{ fontFamily: 'var(--font-heading)', color: timeLeft <= 5 ? 'var(--accent-cyan)' : 'var(--text-primary)' }}>
          {timeLeft}s
        </p>
        <div className="hud-scanline" />
      </div>
      <p className="text-xs mb-3 text-center" style={{ color: 'var(--text-secondary)' }}>1 = HOECHSTE PRIORITAET</p>
      <div className="space-y-2">
        {items.map((item, idx) => (
          <motion.div key={item.id} layout className="flex items-center gap-3 p-3"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}>
            <span className="w-7 h-7 flex items-center justify-center flex-shrink-0 text-mono font-bold"
              style={{ border: '1px solid var(--border-default)', fontSize: '0.8rem' }}>{idx + 1}</span>
            <p className="text-sm flex-1">{item.text}</p>
            <div className="flex flex-col gap-0.5 flex-shrink-0">
              <button onClick={() => moveItem(idx, -1)} disabled={idx === 0}
                className="p-1 hover:bg-black/5 disabled:opacity-20 transition-colors"
                style={{ border: '1px solid var(--border-default)', background: 'transparent' }}>
                <CaretUp size={14} weight="bold" />
              </button>
              <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1}
                className="p-1 hover:bg-black/5 disabled:opacity-20 transition-colors"
                style={{ border: '1px solid var(--border-default)', background: 'transparent' }}>
                <CaretDown size={14} weight="bold" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
      <button onClick={submitEarly} className="page-shell-btn page-shell-btn-outline w-full mt-4 flex items-center justify-center gap-2" data-testid="priority-submit">
        <Check size={18} /> FERTIG
      </button>
    </motion.div>
  );
};

// ==========================================
// SECTION 2B: TRICK QUESTIONS
// ==========================================
const SectionTricks = ({ onComplete, pool }) => {
  const questions = pool && pool.length ? pool : TRICK_QUESTIONS;
  const [currentQ, setCurrentQ] = useState(0);
  const [results, setResults] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const [timedOut, setTimedOut] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  // Start 15s timer per question
  useEffect(() => {
    if (showExplanation || scanning) return;
    setTimeLeft(15);
    setTimedOut(false);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearTimer();
          // Timeout = intuitiver Fehler
          const q = questions[currentQ];
          setTimedOut(true);
          setResults(prev => [...prev, { id: q.id, correct: false, trap_chosen: false, chosen: -1, timeout: true }]);
          setShowExplanation(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return clearTimer;
    // Absichtlich nur [currentQ, scanning]: der 15s-Timer wird pro Frage neu gesetzt.
    // showExplanation/questions hier in die Deps zu nehmen würde den Timer mitten in der Frage neu starten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQ, scanning]);

  const handleAnswer = (optionIdx) => {
    if (showExplanation || scanning) return;
    clearTimer();
    setSelectedOption(optionIdx);
    setShowExplanation(true);

    const q = questions[currentQ];
    const isCorrect = optionIdx === q.correct;
    const isTrap = optionIdx === q.trap;
    setResults(prev => [...prev, { id: q.id, correct: isCorrect, trap_chosen: isTrap, chosen: optionIdx, timeout: false }]);
  };

  // After explanation shown, advance with SCANNING overlay
  useEffect(() => {
    if (!showExplanation) return;
    const timeout = setTimeout(() => {
      setShowExplanation(false);
      setSelectedOption(null);
      setTimedOut(false);
      setScanning(true);
      // Show SCANNING overlay then advance
      setTimeout(() => {
        setScanning(false);
        if (currentQ < questions.length - 1) {
          setCurrentQ(c => c + 1);
        } else {
          // All 5 questions answered — send results
          onComplete(results);
        }
      }, 1200);
    }, 2500);
    return () => clearTimeout(timeout);
    // Absichtlich nur [showExplanation]: der Advance-Timer läuft genau einmal pro Erklärungs-Phase.
    // currentQ/results/onComplete in die Deps zu nehmen würde den Timeout neu aufsetzen und doppelt advancen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExplanation]);

  const q = questions[currentQ];

  // SCANNING overlay
  if (scanning) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="flex flex-col items-center justify-center py-20" data-testid="scanning-overlay">
        <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity }}>
          <Crosshair size={56} style={{ color: 'var(--accent-cyan)' }} />
        </motion.div>
        <p className="text-sm mt-4" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.3em' }}>SCANNING...</p>
        <motion.div className="w-40 h-0.5 mt-3" style={{ background: 'var(--border-default)' }}>
          <motion.div className="h-full" style={{ background: 'var(--accent-cyan)' }}
            animate={{ width: ['0%', '100%'] }} transition={{ duration: 1, ease: 'linear' }} />
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="section-tricks">
      <div className="text-center mb-6">
        <Brain size={48} className="mx-auto mb-4" style={{ color: '#EF4444' }} />
        <h2 className="text-xl font-bold mb-2">STATION B: HEURISTIK-LABOR</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          System 1 vs. System 2 — Kannst du deinen Autopiloten ausschalten?
        </p>
      </div>

      {/* Progress */}
      <div className="flex gap-1 mb-4">
        {questions.map((_, idx) => (
          <div key={idx} className="flex-1 h-1"
            style={{ background: idx < currentQ ? (results[idx]?.correct ? '#22C55E' : 'var(--accent-cyan)') : idx === currentQ ? 'var(--text-primary)' : 'var(--border-default)' }} />
        ))}
      </div>

      {/* Timer */}
      <div className="hud-display text-center mb-4">
        <p className="text-overline" style={{ fontSize: '0.55rem' }}>ZEITLIMIT</p>
        <p className="text-3xl font-black" data-testid="trick-timer"
          style={{ fontFamily: 'var(--font-heading)', color: timeLeft <= 5 ? 'var(--accent-cyan)' : 'var(--text-primary)' }}>
          {showExplanation ? '—' : `${timeLeft}s`}
        </p>
        {!showExplanation && (
          <div className="w-full h-0.5 mt-1" style={{ background: 'var(--border-default)' }}>
            <motion.div className="h-full" style={{ background: timeLeft <= 5 ? 'var(--accent-cyan)' : '#EF4444' }}
              animate={{ width: `${(timeLeft / 15) * 100}%` }} transition={{ duration: 0.3 }} />
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={currentQ} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
          <HudFrame label={`FRAGE ${currentQ + 1}/${questions.length}`} color="#EF4444">
            <p className="text-lg font-bold mb-5" style={{ fontFamily: 'var(--font-heading)', lineHeight: 1.5 }}>{q.question}</p>
            <div className="space-y-2">
              {q.options.map((opt, idx) => {
                let borderColor = 'var(--border-default)';
                let bg = 'var(--bg-surface)';
                if (showExplanation && !timedOut) {
                  if (idx === q.correct) { borderColor = '#22C55E'; bg = 'rgba(34,197,94,0.1)'; }
                  else if (idx === selectedOption && idx !== q.correct) { borderColor = 'var(--accent-cyan)'; bg = 'rgba(239,68,68,0.1)'; }
                }
                if (timedOut && idx === q.correct) {
                  borderColor = '#22C55E'; bg = 'rgba(34,197,94,0.1)';
                }
                return (
                  <button key={idx} onClick={() => handleAnswer(idx)} disabled={showExplanation}
                    className="w-full text-left p-3 text-sm transition-all"
                    style={{ background: bg, border: `1px solid ${borderColor}`, cursor: showExplanation ? 'default' : 'pointer' }}
                    data-testid={`trick-option-${idx}`}>
                    <span className="mr-3" style={{ color: 'var(--text-secondary)' }}>{String.fromCharCode(65 + idx)}.</span>
                    {opt}
                  </button>
                );
              })}
            </div>
          </HudFrame>

          {showExplanation && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-3" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}>
              <p className="text-xs" style={{ color: timedOut ? '#F59E0B' : (selectedOption === q.correct ? '#22C55E' : 'var(--accent-cyan)') }}>
                {timedOut
                  ? 'TIMEOUT — System 1 hat zu lange gezögert. Intuitiver Fehler.'
                  : (selectedOption === q.correct
                    ? 'KORREKT — System 2 hat gewonnen.'
                    : 'SYSTEM 1 FEHLER — Die intuitive Antwort war falsch.')}
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{q.explanation}</p>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

// ==========================================
// MEMORY TEST
// ==========================================
const SectionMemory = ({ onComplete }) => {
  const [phase, setPhase] = useState('intro');
  const [symbols] = useState(() => [...MEMORY_SYMBOLS].sort(() => 0.5 - Math.random()));
  const [targetIdx] = useState(3); // Position 4 (0-indexed = 3)
  const [options, setOptions] = useState([]);
  const [timeLeft, setTimeLeft] = useState(3);
  const intervalRef = useRef(null);

  useEffect(() => { return () => clearInterval(intervalRef.current); }, []);

  const startTest = () => {
    setPhase('show');
    intervalRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(intervalRef.current);
          // Generate options
          const correct = symbols[targetIdx];
          const distractors = MEMORY_DISTRACTOR.filter(s => s !== correct).sort(() => 0.5 - Math.random()).slice(0, 3);
          const opts = [correct, ...distractors].sort(() => 0.5 - Math.random());
          setOptions(opts);
          setPhase('recall');
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  const handleAnswer = (sym) => {
    const isCorrect = sym === symbols[targetIdx];
    onComplete(isCorrect);
  };

  if (phase === 'intro') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="memory-intro">
        <Eye size={48} className="mx-auto mb-4" style={{ color: '#8B5CF6' }} />
        <h3 className="text-xl font-bold mb-2">KURZZEIT-GEDAECHTNIS</h3>
        <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
          7 Symbole erscheinen fuer 3 Sekunden. Merke dir das <span style={{ color: '#8B5CF6' }}>4. Symbol</span>.
        </p>
        <button onClick={startTest} className="btn-brutal btn-brutal-filled flex items-center justify-center gap-3 mx-auto" data-testid="memory-start">
          <Play size={20} weight="fill" /> BEREIT
        </button>
      </motion.div>
    );
  }

  if (phase === 'show') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="memory-show">
        <p className="text-sm mb-4" style={{ color: 'var(--accent-cyan)' }}>MERKE DIR SYMBOL #{targetIdx + 1}</p>
        <div className="flex justify-center gap-4 mb-4">
          {symbols.map((sym, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: idx * 0.1 }}
              className="w-14 h-14 flex items-center justify-center text-3xl"
              style={{
                border: `2px solid ${idx === targetIdx ? '#8B5CF6' : 'var(--border-default)'}`,
                background: idx === targetIdx ? 'rgba(139,92,246,0.1)' : 'var(--bg-surface)'
              }}>
              {sym}
            </motion.div>
          ))}
        </div>
        <p className="text-mono text-xl font-black" style={{ color: 'var(--accent-cyan)' }}>{timeLeft}s</p>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="memory-recall">
      <p className="text-sm mb-4">Welches Symbol war an Position {targetIdx + 1}?</p>
      <div className="flex justify-center gap-4">
        {options.map((sym, idx) => (
          <motion.button key={idx} whileTap={{ scale: 0.9 }} onClick={() => handleAnswer(sym)}
            className="w-16 h-16 flex items-center justify-center text-3xl transition-all hover:bg-black/5"
            style={{ border: '2px solid var(--border-default)', background: 'var(--bg-surface)' }}
            data-testid={`memory-option-${idx}`}>
            {sym}
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
};

// ==========================================
// SECTION 2C: REACTION TEST (Click on Green)
// ==========================================
const SectionReaction = ({ onComplete }) => {
  const [phase, setPhase] = useState('intro');
  const [round, setRound] = useState(0);
  const [isGreen, setIsGreen] = useState(false);
  const [greenTime, setGreenTime] = useState(null);
  const [results, setResults] = useState([]);
  const [tooEarly, setTooEarly] = useState(false);
  const timeoutRef = useRef(null);
  const totalRounds = 5;

  useEffect(() => { return () => clearTimeout(timeoutRef.current); }, []);

  const startRound = () => {
    setIsGreen(false);
    setTooEarly(false);
    setGreenTime(null);
    const delay = 1500 + Math.random() * 3000;
    timeoutRef.current = setTimeout(() => {
      setIsGreen(true);
      setGreenTime(Date.now());
    }, delay);
  };

  const startTest = () => { setPhase('active'); setRound(0); startRound(); };

  const handleClick = () => {
    if (!isGreen) {
      clearTimeout(timeoutRef.current);
      setTooEarly(true);
      setTimeout(() => startRound(), 1000);
      return;
    }
    const rt = Date.now() - greenTime;
    const newResults = [...results, rt];
    setResults(newResults);
    if (round < totalRounds - 1) {
      setRound(r => r + 1);
      setTimeout(startRound, 500);
    } else {
      const avg = Math.round(newResults.reduce((a, b) => a + b, 0) / newResults.length);
      onComplete({ times: newResults, avg_ms: avg });
      setPhase('done');
    }
  };

  if (phase === 'intro') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="reaction-intro">
        <Lightning size={48} className="mx-auto mb-4" style={{ color: '#22C55E' }} />
        <h2 className="text-xl font-bold mb-2">STATION C: REAKTIONS-LATENZ</h2>
        <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
          Klicke so schnell wie moeglich, wenn das Feld <span style={{ color: '#22C55E' }}>GRUEN</span> wird. Nicht vorher!
        </p>
        <button onClick={startTest} className="btn-brutal btn-brutal-filled flex items-center justify-center gap-3 mx-auto" data-testid="reaction-start">
          <Play size={20} weight="fill" /> 5 RUNDEN STARTEN
        </button>
      </motion.div>
    );
  }

  if (phase === 'done') {
    const avg = Math.round(results.reduce((a, b) => a + b, 0) / results.length);
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="reaction-done">
        <Check size={48} className="mx-auto mb-3" style={{ color: '#22C55E' }} />
        <p className="text-mono mb-2" style={{ color: '#22C55E' }}>REAKTIONS-TEST ABGESCHLOSSEN</p>
        <p className="text-3xl font-black" style={{ fontFamily: 'var(--font-heading)', color: avg < 300 ? '#22C55E' : avg < 400 ? '#F59E0B' : 'var(--accent-cyan)' }}>{avg}ms</p>
        <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Durchschnittliche Reaktionszeit</p>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="reaction-active">
      <p className="text-sm mb-3">Runde {round + 1}/{totalRounds}</p>
      <motion.button onClick={handleClick}
        className="w-full h-52 flex items-center justify-center transition-all"
        style={{
          background: tooEarly ? 'var(--accent-cyan)' : isGreen ? '#22C55E' : 'var(--bg-surface)',
          border: '2px solid var(--border-default)',
          cursor: 'pointer'
        }}
        data-testid="reaction-target">
        {tooEarly ? (
          <p className="text-mono font-bold text-black">ZU FRUEH!</p>
        ) : isGreen ? (
          <p className="text-mono font-bold text-black text-2xl">JETZT KLICKEN!</p>
        ) : (
          <motion.p className="text-mono" style={{ color: 'var(--text-secondary)' }} animate={{ opacity: [0.3, 0.8, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity }}>
            WARTE AUF GRUEN...
          </motion.p>
        )}
      </motion.button>
      {results.length > 0 && (
        <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
          Letzte: {results[results.length - 1]}ms
        </p>
      )}
    </motion.div>
  );
};

// ==================== MAIN COMPONENT ====================
const PhysicalAssessment = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [physicalData, setPhysicalData] = useState({
    max_pushups: '', max_plank_seconds: '', weekly_km: '', daily_steps: '', resting_pulse: ''
  });
  const [priorityResult, setPriorityResult] = useState(null);
  const [trickResults, setTrickResults] = useState([]);
  const [memoryResult, setMemoryResult] = useState(null);
  const [reactionResult, setReactionResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Per-session pool picks: deterministisch anhand sessionId → derselbe User sieht bei Refresh die gleichen 5, beim nächsten Deepdive andere
  const sessionTrickPool = React.useMemo(
    () => pickRandom(TRICK_QUESTIONS_POOL, 5, `${sessionId}_tricks`),
    [sessionId]
  );
  const sessionPriorityPool = React.useMemo(
    () => pickRandom(PRIORITY_SCENARIOS_POOL, 5, `${sessionId}_priority`).map((s, i) => ({ ...s, urgency: i + 1 })),
    [sessionId]
  );

  const sections = [
    { name: 'PHYSISCHES PROFIL', icon: Barbell, color: '#22C55E' },
    { name: 'PRIORITAETEN', icon: Target, color: '#F59E0B' },
    { name: 'HEURISTIK-LAB', icon: Brain, color: '#EF4444' },
    { name: 'GEDAECHTNIS', icon: Eye, color: '#8B5CF6' },
    { name: 'REAKTION', icon: Lightning, color: '#22C55E' }
  ];

  const advance = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(c => c + 1);
      window.scrollTo(0, 0);
    } else {
      submitAll();
    }
  };

  const submitAll = async () => {
    setSubmitting(true);
    try {
      const physScore = calcPhysicalScore(physicalData);
      const trickCorrect = trickResults.filter(r => r.correct).length;
      const biasResistanceScore = trickCorrect; // 0-5 scale
      const cogScore = Math.round(
        (trickCorrect / sessionTrickPool.length) * 40 +
        (priorityResult?.matches || 0) * 6 +
        (memoryResult ? 15 : 0) +
        Math.max(0, 15 - Math.max(0, (reactionResult?.avg_ms || 400) - 200) / 20)
      );

      const totalHEI = Math.round((physScore + cogScore) / 2);

      const payload = {
        session_id: sessionId,
        max_pushups: Number(physicalData.max_pushups) || 0,
        max_plank_seconds: Number(physicalData.max_plank_seconds) || 0,
        weekly_km: Number(physicalData.weekly_km) || 0,
        daily_steps: Number(physicalData.daily_steps) || 0,
        resting_pulse: Number(physicalData.resting_pulse) || 0,
        priority_results: priorityResult || {},
        trick_results: trickResults,
        trick_correct: trickCorrect,
        trick_total: sessionTrickPool.length,
        bias_resistance_score: biasResistanceScore,
        memory_correct: memoryResult || false,
        reaction_times: reactionResult?.times || [],
        reaction_avg_ms: reactionResult?.avg_ms || 0,
        physical_score: physScore,
        cognitive_score: cogScore,
        total_hei: totalHEI
      };

      await axios.post(`${API}/user/complete-physical`, payload);
      await axios.post(`${API}/user/complete-block`, { session_id: sessionId, block_id: 'koerper', answers: payload });
      navigate(`/dashboard/${sessionId}`);
    } catch (err) {
      console.error('Error:', err);
      setSubmitting(false);
    }
  };

  if (submitting) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-void)' }}>
        <NoiseOverlay />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
            <Fingerprint size={64} className="mx-auto mb-4" />
          </motion.div>
          <p className="text-mono" style={{ color: 'var(--text-secondary)' }}>HARDWARE-PROFIL WIRD GESPEICHERT...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="page-shell-bg" data-testid="physical-assessment">
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-2xl page-shell-card">
        <div className="page-shell-header">
          <div className="flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: sections[currentSection].color }}>
              <Crosshair size={20} weight="bold" color="#fff" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Block 2: Koerper</h2>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Hardware Profiling & Kognitives Lab</p>
            </div>
          </div>
          <span className="text-xs px-3 py-1 font-semibold" style={{ border: '1px solid var(--border-default)', borderRadius: '9999px', color: sections[currentSection].color }}>
            {sections[currentSection].name}
          </span>
        </div>
        <div className="h-1" style={{ background: 'var(--border-default)' }}>
          <div className="h-full" style={{ width: `${((currentSection + 1) / sections.length) * 100}%`, background: sections[currentSection].color, borderRadius: '0 4px 4px 0', transition: 'width 0.4s' }} />
        </div>
        <div className="page-shell-body">
          <AnimatePresence mode="wait">
            {currentSection === 0 && <SectionPhysical key="phys" data={physicalData} setData={setPhysicalData} onComplete={advance} />}
            {currentSection === 1 && <SectionPriority key="prio" pool={sessionPriorityPool} onComplete={(result) => { setPriorityResult(result); advance(); }} />}
            {currentSection === 2 && <SectionTricks key="tricks" pool={sessionTrickPool} onComplete={(results) => { setTrickResults(results); advance(); }} />}
            {currentSection === 3 && <SectionMemory key="mem" onComplete={(correct) => { setMemoryResult(correct); advance(); }} />}
            {currentSection === 4 && <SectionReaction key="react" onComplete={(result) => { setReactionResult(result); advance(); }} />}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default PhysicalAssessment;
